/**
 * Utility functions for handling recurrence patterns in blockholds
 */

function parseTimeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + (minutes || 0);
}

/**
 * Checks whether an exclusion/block overlaps a 60-minute availability slot on a given date.
 */
export function exclusionOverlapsSlot(
  checkDate: string,
  checkTime: string,
  blockhold: any
): boolean {
  const blockedStartDateTime = blockhold.startdatetime;
  if (!blockedStartDateTime || typeof blockedStartDateTime !== 'string') {
    return false;
  }

  if (!blockedStartDateTime.includes('T')) {
    return false;
  }

  const recurrence = blockhold.recurrence;
  const recurrenceType = recurrence?.recurrenceType ?? 0;
  const [, timePart] = blockedStartDateTime.split('T');
  const blockedTime = timePart.split(':').slice(0, 2).join(':');
  const duration = blockhold.duration || 60;

  const blockStartMinutes = parseTimeToMinutes(blockedTime);
  const blockEndMinutes = blockStartMinutes + duration;
  const checkStartMinutes = parseTimeToMinutes(checkTime);
  const checkEndMinutes = checkStartMinutes + 60;

  if (checkStartMinutes >= blockEndMinutes || checkEndMinutes <= blockStartMinutes) {
    return false;
  }

  if (recurrenceType === 0) {
    const [blockedDate] = blockedStartDateTime.split('T');
    return blockedDate === checkDate;
  }

  return matchesRecurrencePattern(checkDate, checkTime, blockhold);
}

/**
 * Checks if a date matches a recurrence pattern for a blockhold
 * @param checkDate - The date to check (YYYY-MM-DD format)
 * @param checkTime - The time to check (HH:MM format)
 * @param blockhold - The blockhold object with recurrence information
 * @returns true if the date/time matches the recurrence pattern
 */
export function matchesRecurrencePattern(
  checkDate: string,
  checkTime: string,
  blockhold: any
): boolean {
  const recurrence = blockhold.recurrence;
  if (!recurrence) {
    return false;
  }

  const recurrenceType = recurrence.recurrenceType;
  const startDateTime = blockhold.startdatetime;
  const endDateTime = blockhold.enddatetime;

  if (!startDateTime) {
    return false;
  }

  // Parse the blockhold's start and end datetime
  const [blockStartDate, blockStartTime] = startDateTime.split('T');
  const blockStartTimeOnly = blockStartTime ? blockStartTime.split(':').slice(0, 2).join(':') : '00:00';
  
  // Get duration in minutes (default to 60 if not provided)
  const duration = blockhold.duration || 60;
  
  // Parse times to minutes since midnight for comparison
  const blockStartMinutes = parseTimeToMinutes(blockStartTimeOnly);
  const blockEndMinutes = blockStartMinutes + duration;
  const checkTimeMinutes = parseTimeToMinutes(checkTime);
  
  // Assume availability slots are 60 minutes (1 hour)
  // Check if the availability time slot overlaps with the blockhold time range
  // Availability: [checkTimeMinutes, checkTimeMinutes + 60]
  // Blockhold: [blockStartMinutes, blockEndMinutes]
  // They overlap if: checkTimeMinutes < blockEndMinutes AND (checkTimeMinutes + 60) > blockStartMinutes
  const availabilityEndMinutes = checkTimeMinutes + 60;
  
  if (checkTimeMinutes >= blockEndMinutes || availabilityEndMinutes <= blockStartMinutes) {
    return false; // No overlap
  }

  // Parse dates
  const checkDateObj = new Date(checkDate + 'T00:00:00');
  const blockStartDateObj = new Date(blockStartDate + 'T00:00:00');
  const blockEndDateObj = endDateTime ? new Date(endDateTime.split('T')[0] + 'T00:00:00') : null;

  // Check if checkDate is within the active period (startdatetime to enddatetime)
  if (checkDateObj < blockStartDateObj) {
    return false;
  }
  if (blockEndDateObj && checkDateObj > blockEndDateObj) {
    return false;
  }

  // Handle different recurrence types
  if (recurrenceType === 0) {
    // One-time block: only match if the exact date matches
    return checkDate === blockStartDate;
  } else if (recurrenceType === 1) {
    // Daily hold: match every day within the active period
    return true;
  } else if (recurrenceType === 2) {
    // Weekly/biweekly hold: check daysofweek mask and interval
    const daysofweek = recurrence.daysofweek;
    const interval = recurrence.interval || 1; // Default to 1 (weekly)

    if (!daysofweek) {
      return false;
    }

    // Convert daysofweek to binary string if it's a number
    // The mask is MTWHFSS with Monday as low-order bit
    // Example: "1010100" = Mon + Wed + Fri
    // The mask order is: [M][T][W][H][F][S][S] (Monday to Sunday, left to right)
    // String indices:     0  1  2  3  4  5  6
    // If daysofweek is a number (e.g., 84), convert to binary: 84 = 1010100
    let daysofweekMask: string;
    if (typeof daysofweek === 'number') {
      // Convert number to 7-bit binary string
      daysofweekMask = daysofweek.toString(2).padStart(7, '0');
    } else {
      // Already a string, just pad to 7 characters
      daysofweekMask = daysofweek.toString().padStart(7, '0');
    }
    
    // Get day of week for checkDate (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const checkDayOfWeek = checkDateObj.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    
    // Map JavaScript day (0=Sun, 1=Mon, ..., 6=Sat) to mask position
    // Mask order: M=0, T=1, W=2, H=3, F=4, S=5, S=6
    // JavaScript: Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
    // So: Mon -> 0, Tue -> 1, Wed -> 2, Thu -> 3, Fri -> 4, Sat -> 5, Sun -> 6
    const maskIndex = checkDayOfWeek === 0 ? 6 : checkDayOfWeek - 1;
    
    // Check if the day is set in the mask
    const isDaySet = daysofweekMask[maskIndex] === '1';
    
    if (!isDaySet) {
      return false;
    }

    // Check interval (biweekly if interval === 2)
    if (interval === 2) {
      // Biweekly: calculate weeks since start date
      const weeksSinceStart = Math.floor((checkDateObj.getTime() - blockStartDateObj.getTime()) / (7 * 24 * 60 * 60 * 1000));
      return weeksSinceStart % 2 === 0; // Only match even weeks
    } else {
      // Weekly: match every week
      return true;
    }
  } else if (recurrenceType === 3) {
    // Monthly hold: check daysofweek (1-4 = 1st-4th, 5 = Last) and recurrenceinterval
    const daysofweek = recurrence.daysofweek; // 1-4 = 1st-4th, 5 = Last
    const recurrenceinterval = recurrence.recurrenceinterval; // 1 = Day, 2 = Weekday, 3-9 = Sun-Sat

    if (!daysofweek || !recurrenceinterval) {
      return false;
    }

    // Check if the month matches (must be in the same month as start or a later month)
    const checkMonth = checkDateObj.getMonth();
    const checkYear = checkDateObj.getFullYear();
    const blockStartMonth = blockStartDateObj.getMonth();
    const blockStartYear = blockStartDateObj.getFullYear();

    // Calculate which occurrence of the month this is
    const monthDiff = (checkYear - blockStartYear) * 12 + (checkMonth - blockStartMonth);
    if (monthDiff < 0) {
      return false;
    }

    // Handle daysofweek (which occurrence in the month)
    let targetDayOfMonth: number | null = null;
    
    if (daysofweek >= 1 && daysofweek <= 4) {
      // 1-4 = 1st-4th occurrence
      targetDayOfMonth = daysofweek;
    } else if (daysofweek === 5) {
      // 5 = Last occurrence
      targetDayOfMonth = -1; // Special marker for last
    } else {
      return false;
    }

    // Handle recurrenceinterval (which day of week)
    const targetDayOfWeek = recurrenceinterval === 1 
      ? null // Day (any day)
      : recurrenceinterval === 2 
      ? null // Weekday (Mon-Fri)
      : recurrenceinterval >= 3 && recurrenceinterval <= 9
      ? recurrenceinterval - 3 // 3=Sun, 4=Mon, ..., 9=Sat
      : null;

    if (targetDayOfWeek !== null) {
      // Check if the day of week matches
      if (checkDateObj.getDay() !== targetDayOfWeek) {
        return false;
      }
    } else if (recurrenceinterval === 2) {
      // Weekday: check if it's Monday-Friday
      const dayOfWeek = checkDateObj.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false; // Not a weekday
      }
    }
    // If recurrenceinterval === 1 (Day), any day is fine

    // Now check if this is the correct occurrence in the month
    if (targetDayOfMonth === -1) {
      // Last occurrence: find the last matching day in the month
      const lastDayOfMonth = new Date(checkYear, checkMonth + 1, 0).getDate();
      let lastMatchingDay = lastDayOfMonth;
      
      // Work backwards to find the last matching day
      for (let day = lastDayOfMonth; day >= 1; day--) {
        const testDate = new Date(checkYear, checkMonth, day);
        let matchesDayType = true;
        
        if (targetDayOfWeek !== null) {
          matchesDayType = testDate.getDay() === targetDayOfWeek;
        } else if (recurrenceinterval === 2) {
          const dow = testDate.getDay();
          matchesDayType = dow !== 0 && dow !== 6; // Weekday
        }
        
        if (matchesDayType) {
          lastMatchingDay = day;
          break;
        }
      }
      
      return checkDateObj.getDate() === lastMatchingDay;
    } else {
      // 1st-4th occurrence: find the Nth matching day
      let occurrenceCount = 0;
      const checkDay = checkDateObj.getDate();
      
      for (let day = 1; day <= checkDay; day++) {
        const testDate = new Date(checkYear, checkMonth, day);
        let matchesDayType = true;
        
        if (targetDayOfWeek !== null) {
          matchesDayType = testDate.getDay() === targetDayOfWeek;
        } else if (recurrenceinterval === 2) {
          const dow = testDate.getDay();
          matchesDayType = dow !== 0 && dow !== 6; // Weekday
        }
        
        if (matchesDayType) {
          occurrenceCount++;
          if (occurrenceCount === targetDayOfMonth && day === checkDay) {
            return true;
          }
        }
      }
      
      return false;
    }
  }

  return false;
}
